import {createCmsApiProvider} from './providers/cmsApiProvider'
import {createDisabledProvider} from './providers/disabledProvider'

export interface PageContext {
  pageTitle?: string
  locale?: string // e.g. 'en' | 'ne'
}

export type Confidence = 'low' | 'med' | 'high'

export interface Suggestion {
  text: string
  confidence: Confidence
  provider: string
}

export interface ProviderCapabilities {
  altText: boolean
  linkText: boolean
  headerLabel: boolean
  tableCaption: boolean
  headingHint: boolean
  ariaLabel: boolean
}

export interface A11yAiProvider {
  readonly id: string
  readonly capabilities: ProviderCapabilities
  suggestAltText(image: Blob | string, ctx?: PageContext): Promise<Suggestion>
  suggestLinkText(href: string, surroundingText: string, ctx?: PageContext): Promise<Suggestion>
  suggestHeaderLabel(columnSamples: string[], ctx?: PageContext): Promise<Suggestion>
  suggestTableCaption(cellSamples: string[], ctx?: PageContext): Promise<Suggestion>
  suggestHeadingHint(sectionText: string, ctx?: PageContext): Promise<Suggestion>
  suggestAriaLabel(elementDescription: string, ctx?: PageContext): Promise<Suggestion>
}

const ALT_MAX = 125

export function buildAltTextFromCaption(caption: string, _ctx?: PageContext): string {
  let text = (caption || '').trim().replace(/\s+/g, ' ')
  if (!text) return ''
  text = text.charAt(0).toUpperCase() + text.slice(1)
  text = text.replace(/\.+$/, '')
  if (text.length > ALT_MAX) {
    text = text.slice(0, ALT_MAX).replace(/\s+\S*$/, '').trim()
  }
  return text
}

export function deriveLinkText(href: string, surroundingText: string): string {
  try {
    const url = new URL(href, 'https://placeholder.invalid')
    const seg = url.pathname.split('/').filter(Boolean).pop()
    const isPathSegment = !!seg
    const source = seg || url.hostname.replace(/^www\./, '')
    const humanized = decodeURIComponent(source)
      .replace(/\.(html?|php|aspx?|pdf|docx?)$/i, '')
      .replace(/[-_]+/g, ' ')
      .trim()
    if (humanized) {
      if (isPathSegment) {
        return humanized.charAt(0).toUpperCase() + humanized.slice(1)
      }
      return humanized
    }
  } catch {
    // fall through to surrounding text
  }
  const fallback = surroundingText.trim().slice(0, 60)
  return fallback || 'View page'
}

export function isAiEnabled(): boolean {
  const flag = (
    window.__RUNTIME_CONFIG__?.REACT_APP_AI_ENABLED ??
    process.env.REACT_APP_AI_ENABLED ??
    ''
  ).toLowerCase()
  return flag === 'true'
}

let cached: A11yAiProvider | null = null

export function getA11yAiProvider(): A11yAiProvider {
  if (!isAiEnabled()) {
    // Never cache the disabled provider — runtime config may change between page loads.
    return createDisabledProvider('disabled')
  }
  if (cached) return cached
  cached = createCmsApiProvider()
  return cached
}

/** Test-only: clears the memoized provider so env changes take effect. */
export function __resetA11yAiProviderCache(): void {
  cached = null
}
